import { Component, OnInit, ViewChild, TemplateRef, inject } from "@angular/core";
import { GenericGroupedListConfig } from "@app/shared/models/GenericList/GenericGroupedListConfig";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { Pages } from "@constants/pages/Pages";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { TabService } from "@services/tabs/TabService";
import { OverviewDataService } from "./shared/adv-eff-overview-data.service";
import { WizardComponent } from "./wizard/adv-eff-wizard.component";
import { EffectivenessControlOverviewModel } from "./shared/effectiveness-control-overview.model";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { finalize } from "rxjs/operators";
import { ControlDataService } from "@app/controls/services/control.data.service";
import { ControlPermissionModel } from "@app/controls/models/ControlPermissionsModel";
import { IdNameColorModel } from "@models/generic/IdNameColorModel";
import { of, firstValueFrom } from "rxjs";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { TabMenuItem } from "@models/generic/TabModels/TabMenuItem";
import { HistoryOverviewComponent } from "@app/shared/history-overview/history-overview.component";
import { AdvEffHistoryDataService } from "./adv-eff-history-data.service";
import { nameof } from "@methods/jeffs-toolkit";
import { TranslocoService, provideTranslocoScope, TranslocoPipe } from "@jsverse/transloco";
import { CerrixLoaderComponent } from "@app/shared/components/cerrix-loader/cerrix-loader.component";
import { MenuSystemDirective } from "@app/shared/directives/menu-system.directive";
import { TestperiodManagerComponent } from "./testperiod-manager/adv-eff-testperiod-manager.component";
import { AuditSamplerComponent } from "./audit-sampler/adv-eff-audit-sampler.component";
import { SharedModule } from "@app/shared/shared.module";
import { ControlModule } from "@app/controls/control.module";

@Component({
    selector: "adv-eff-control-overview",
    templateUrl: "./adv-eff-control-overview.component.html",
    styleUrls: ["./adv-eff-control-overview.component.scss"],
    providers: [provideTranslocoScope("controls")],
    imports: [
        CerrixLoaderComponent,
        MenuSystemDirective,
        TranslocoPipe,
        WizardComponent,
        HistoryOverviewComponent,
        TestperiodManagerComponent,
        AuditSamplerComponent,
        SharedModule,
        ControlModule,
    ],
})
export class AdvEffControlOverviewComponent implements OnInit {
    private ds = inject(OverviewDataService);
    private pages = inject(Pages);
    private tabService = inject(TabService);
    private modalService = inject(BsModalService);
    private controlDs = inject(ControlDataService);
    advancedEffectivenessHistoryDataService = inject(AdvEffHistoryDataService);
    private _promptService = inject(CerrixPromptService);
    private _transloco = inject(TranslocoService);

    @ViewChild("testWizard") testWizard: WizardComponent;
    @ViewChild(HistoryOverviewComponent) historyOverviewComponent;

    tab: TabModel;
    id: any;
    effectivenessOverviewConfig: GenericListConfig;
    modalRef: BsModalRef;
    permissions: ControlPermissionModel;
    scores: IdNameColorModel[];
    hideActionBar = false;

    async ngOnInit(): Promise<void> {
        await firstValueFrom(this._transloco.load(`controls/${this._transloco.getActiveLang()}`));

        this.controlDs.getPermissions(this.id).subscribe(
            (perm) => {
                this.permissions = perm;
                this.controlDs.getAllScores().subscribe((d) => {
                    this.scores = d;
                    this.createOverviewConfig();

                    this.tab.showLoader = false;
                });
            },
            () => this.tab.close(false)
        );
    }

    createOverviewConfig(): void {
        this.effectivenessOverviewConfig = <GenericGroupedListConfig>{
            name: this._transloco.translate("controls.advEffControlOverview.testPlansListName"),
            groupByProp: nameof<EffectivenessControlOverviewModel>((eff) => eff.groupId),
            groupByDisplayName: nameof<EffectivenessControlOverviewModel>((eff) => eff.groupName),
            overviewSortBy: nameof<EffectivenessControlOverviewModel>((eff) => eff.endDate),
            overviewSortAsc: true,

            idProp: nameof<EffectivenessControlOverviewModel>((eff) => eff.guid),

            allowAdd: this.permissions.CanManageEffectivenesses,
            allowEdit: this.permissions.CanManageEffectivenesses,
            allowDelete: this.permissions.CanManageEffectivenesses,

            fields: [
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>((eff) => eff.testPlanName),
                    prettyName: this._transloco.translate("controls.advEffControlOverview.colTestPlanName"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>(
                        (eff) => eff.statusDescription
                    ),
                    prettyName: this._transloco.translate("controls.advEffControlOverview.colStatus"),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>((eff) => eff.startDate),
                    prettyName: this._transloco.translate("controls.advEffControlOverview.colStartDate"),
                    fieldType: GenericListFieldType.Date,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>((eff) => eff.endDate),
                    prettyName: this._transloco.translate("controls.advEffControlOverview.colEndDate"),
                    fieldType: GenericListFieldType.Date,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>(
                        (eff) => eff.numberOfSamples
                    ),
                    prettyName: this._transloco.translate("controls.advEffControlOverview.colNumSamples"),
                    fieldType: GenericListFieldType.Number,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>(
                        (eff) => eff.numberOfSourceDocuments
                    ),
                    prettyName: this._transloco.translate("controls.advEffControlOverview.colNumSourceDocuments"),
                    fieldType: GenericListFieldType.Number,
                },
                {
                    fieldName: nameof<EffectivenessControlOverviewModel>((eff) => eff.score),
                    prettyName: this._transloco.translate("controls.advEffControlOverview.colScore"),
                    fieldType: GenericListFieldType.ColoredSelect,
                    getDataMethod: () => {
                        return of(this.scores);
                    },
                },
            ],

            cellConfig: {
                testPlanName: {
                    alignLeft: true,
                },
            },

            cellSummaries: {
                testPlanName: {
                    cellStringFormat: "[embedGroupName] ([embedCount] Test plans)",
                    embedGroupName: true,
                    embedCount: true,
                },
                startDate: {
                    cellStringFormat:
                        "{{" +
                        nameof<EffectivenessControlOverviewModel>((eff) => eff.groupStartDate) +
                        "}}",
                },
                endDate: {
                    cellStringFormat:
                        "{{" +
                        nameof<EffectivenessControlOverviewModel>((eff) => eff.groupEndDate) +
                        "}}",
                },
                numberOfSamples: {
                    cellStringFormat: "[embedSum]/[embedAvg]",
                    embedSum: true,
                    embedAvg: nameof<EffectivenessControlOverviewModel>(
                        (eff) => eff.groupMinimumSampleCount
                    ), // This will be minimum sample count in future.
                },
                numberOfSourceDocuments: {
                    cellStringFormat: "[embedSum]",
                    embedSum: true,
                },
                score: {
                    embedColor: { colorCol: "groupScoreColor", textCol: "groupScoreText" },
                },
            },

            overviewRowActionsName: this._transloco.translate("controls.advEffControlOverview.detailsActionsName"),
            overviewRowActions: [
                {
                    icon: "fas fa-edit",
                    tooltip: this._transloco.translate("controls.advEffControlOverview.editTestPlanTooltip"),
                    clickEvent: (row: EffectivenessControlOverviewModel) => {
                        this.testWizard.effectivenessGuid = row.guid;
                        this.testWizard.load(true);
                    },
                },
            ],

            addOverride: async () => {
                this.testWizard.effectivenessGuid = null;
                await this.testWizard.load(true);
            },
            editOverride: (row: any) => {
                this.tabService.generateTab(this.pages.ControlsTestingAdvEffDetail, row.guid);
                return false;
            },
            delete: async (row: any) => {
                if (
                    await this._promptService
                        .confirm(
                            this._transloco.translate("controls.advEffControlOverview.deleteTestPlanTitle"),
                            this._transloco.translate("controls.advEffControlOverview.deleteTestPlanMessage")
                        )
                        .toPromise()
                ) {
                    const deletingPrompt = this._promptService.loader(
                        this._transloco.translate("controls.advEffControlOverview.deletingLoader")
                    );

                    await firstValueFrom(this.ds
                        .deleteEffectiveness(row.guid)
                        .pipe(finalize(() => deletingPrompt.close())));

                    this.effectivenessOverviewConfig.reload();
                    return true;
                }

                return false;
            },
            dataMethod: () => this.ds.getControlEffectivenesses(this.id, this.scores),
        };
    }

    async reload() {
        await this.effectivenessOverviewConfig.reload();
    }

    openModal(template: TemplateRef<any>) {
        this.modalRef = this.modalService.show(template, {
            class: "modal-xl",
            animated: true,
            keyboard: false,
            ignoreBackdropClick: true,
        });
    }

    onMenuItemClick(menuItem: TabMenuItem) {
        if (!menuItem || !menuItem.menuItemId) {
            return;
        }

        this.hideActionBar = menuItem.menuItemId !== "effectiveness-menu-item";

        switch (menuItem.menuItemId) {
            case "historymenuitem": {
                this.historyOverviewComponent.loadHistory();
                break;
            }
        }
    }
}
